/*!
 * TinyPoker EcoBot 數據分析
 * 追蹤和分析 EcoBot 帳號的營收數據
 */
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Table,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import axios from "axios";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_BASE = "/api/tiny";
const ECOBOT_PARTNER_ID = "ecobot";

// EcoBot RG 佔成。歷史合約值 30%，**刻意不開放設定**（能改它等於能竄改已結的帳）。
// 新費率與生效週由本頁「配置管理」Tab 的〈EcoBot cash 佔成設定〉卡片維護，存在
// system_settings.ecobot_rate_settings；未設生效週 → 一律用歷史值（fail-closed）。
// 後端對應：partner_revenue_formula_config.PartnerRevenueFormula.get_rg_net_rate()
//
// ⚠️ 這跟本頁的「聯盟JP降波動」30%/50%（jpVolRate）是兩件完全不同的事，別混用。
const RG_HISTORICAL_RATE = 0.30;
const WEEK_ID_RE = /^\d{8}-\d{8}$/;

const getRgNetRate = (week, rateSettings) => {
  if (!week || !rateSettings) return RG_HISTORICAL_RATE;
  const eff = rateSettings.effective_from_week;
  if (!eff) return RG_HISTORICAL_RATE;
  if (String(week).slice(0, 8) < String(eff).slice(0, 8)) return RG_HISTORICAL_RATE;
  const r = Number(rateSettings.new_rate);
  if (!Number.isFinite(r)) return RG_HISTORICAL_RATE;
  return r > 1 ? r / 100 : r;
};

// 格式化數字
const numberWithCommas = (num) => {
  if (num === null || num === undefined) return "0";
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// 遊戲類型配置（含顏色）
const gameTypeConfig = {
  ring_game: { 
    name: 'NLH/PLO', 
    color: '#1f8ef1',
    bgColor: 'rgba(31, 142, 241, 0.1)'
  },
  aof: { 
    name: 'AOF', 
    color: '#00d6b4',
    bgColor: 'rgba(0, 214, 180, 0.1)'
  },
  sng: { 
    name: 'SNG', 
    color: '#fd5d93',
    bgColor: 'rgba(253, 93, 147, 0.1)'
  },
  mtt: { 
    name: 'MTT', 
    color: '#f17e5d',
    bgColor: 'rgba(241, 126, 93, 0.1)'
  }
};

function TinyEcoBot() {
  // State
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [selectedWeek, setSelectedWeek] = useState("");
  // EcoBot 佔成設定（新費率 + 生效週）；未設生效週 = 尚未生效 = 用歷史 30%
  const [rateSettings, setRateSettings] = useState(null);
  const [rateDraft, setRateDraft] = useState({ new_rate: "", effective_from_week: "" });
  const [rateSaving, setRateSaving] = useState(false);
  const [rateMsg, setRateMsg] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [weekStats, setWeekStats] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [config, setConfig] = useState(null);
  const [snapshotConfig, setSnapshotConfig] = useState(null);  // v2.2.0: 當週 snapshot 配置（含 nickname）
  const [configForm, setConfigForm] = useState({
    partner_name: "",
    super_agent_ids: "",
    club_ids: "",
    mixed_club_ids: "",
    description: "",
    status: "active",
  });
  const [configSaving, setConfigSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [configEditMode, setConfigEditMode] = useState(false);
  // 聯盟JP降波動比例：5/11 起可切 30%/50%（預設 50%），之前的週固定 30%
  const [jpVolRate, setJpVolRate] = useState(0.50);
  
  // Chart options
  const getLineChartOptions = (color) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { 
          color: '#fff',
          font: { size: 12 },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += numberWithCommas(context.parsed.y);
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { 
          color: '#9A9A9A',
          font: { size: 10 }
        },
        grid: { 
          color: 'rgba(255,255,255,0.05)',
          display: false 
        }
      },
      y: {
        ticks: { 
          color: '#9A9A9A',
          font: { size: 10 },
          callback: function(value) {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(0) + 'K';
            }
            return value;
          }
        },
        grid: { 
          color: 'rgba(255,255,255,0.1)',
          lineWidth: 0.5
        }
      }
    }
  });

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: { 
          color: '#fff', 
          padding: 15,
          font: { size: 12 },
          boxWidth: 15,
          boxHeight: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            const total = context.dataset.data.reduce((a,b) => a+b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            label += numberWithCommas(context.parsed) + ' (' + percentage + '%)';
            return label;
          }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: { 
          color: '#fff',
          font: { size: 12 },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += numberWithCommas(context.parsed.y);
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { 
          color: '#9A9A9A',
          font: { size: 10 }
        },
        grid: { display: false }
      },
      y: {
        ticks: { 
          color: '#9A9A9A',
          font: { size: 10 },
          callback: function(value) {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(0) + 'K';
            }
            return value;
          }
        },
        grid: { 
          color: 'rgba(255,255,255,0.1)',
          lineWidth: 0.5
        }
      }
    }
  };

  // 載入初始數據
  useEffect(() => {
    loadInitialData();
    loadRateSettings();
  }, []);

  // ===== EcoBot 佔成設定 =====
  const loadRateSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/system-settings/ecobot_rate_settings`);
      const v = res.data.code === 1 ? (res.data.data.value || {}) : {};
      const normalized = {
        new_rate: v.new_rate ?? null,
        effective_from_week: v.effective_from_week ?? null,
      };
      setRateSettings(normalized);
      setRateDraft({
        new_rate: normalized.new_rate ?? "",
        effective_from_week: normalized.effective_from_week ?? "",
      });
    } catch (err) {
      // 讀不到就維持 null → getRgNetRate 回歷史 30%（fail-closed）
      console.warn("載入 EcoBot 佔成設定失敗，沿用歷史費率 30%", err);
      setRateSettings(null);
    }
  };

  const saveRateSettings = async () => {
    setRateSaving(true); setRateMsg(null);
    try {
      const payload = {
        new_rate: rateDraft.new_rate === "" ? null : Number(rateDraft.new_rate),
        effective_from_week: rateDraft.effective_from_week || null,
      };
      const res = await axios.put(
        `${API_BASE}/system-settings/ecobot_rate_settings`, { value: payload });
      if (res.data.code !== 1) throw new Error(res.data.msg);
      setRateSettings(payload);
      setRateMsg({ ok: true, text: "已儲存。需重跑該週 pipeline 才會反映到報表數字。" });
      loadWeekData(selectedWeek);
    } catch (err) {
      setRateMsg({ ok: false, text: "儲存失敗：" + (err.response?.data?.msg || err.message) });
    } finally {
      setRateSaving(false);
    }
  };

  useEffect(() => {
    if (!config) return;
    setConfigForm({
      partner_name: config.partner_name || "",
      super_agent_ids: (config.super_agent_ids || []).join(", "),
      club_ids: (config.club_ids || []).join(", "),
      mixed_club_ids: (config.mixed_club_ids || []).join(", "),
      description: config.description || "",
      status: config.status || "active",
    });
  }, [config]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [weeksRes, trendRes, configRes] = await Promise.all([
        axios.get(`${API_BASE}/ecobot/weeks?limit=20`),
        axios.get(`${API_BASE}/ecobot/trend?weeks=12`),
        axios.get(`${API_BASE}/partners/${ECOBOT_PARTNER_ID}`),
      ]);

      if (weeksRes.data.code === 1) {
        const weeksList = weeksRes.data.data || [];
        setWeeks(weeksList);
        if (weeksList.length > 0) {
          setSelectedWeek(weeksList[0]);
          loadWeekData(weeksList[0]);
        }
      }
      
      if (trendRes.data.code === 1) {
        setTrendData(trendRes.data.data || []);
      }
      
      if (configRes.data.code === 1) {
        setConfig(configRes.data.data || null);
      }
    } catch (err) {
      console.error("載入數據失敗:", err);
      setError("載入數據失敗: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadWeekData = async (week) => {
    try {
      const [statsRes, snapRes] = await Promise.all([
        axios.get(`${API_BASE}/ecobot/stats/${week}`),
        axios.get(`${API_BASE}/ecobot/snapshot/${week}`),
      ]);
      if (statsRes.data.code === 1) setWeekStats(statsRes.data.data);
      if (snapRes.data.code === 1) setSnapshotConfig(snapRes.data.data);
    } catch (err) {
      console.error("載入周數據失敗:", err);
    }
  };

  useEffect(() => {
    if (selectedWeek) {
      loadWeekData(selectedWeek);
    }
  }, [selectedWeek]);

  // 切週時重設降波動比例預設：5/11 起預設 50%，之前的週固定 30%
  useEffect(() => {
    const wkStart = (selectedWeek || "").split("-")[0];
    setJpVolRate(wkStart >= "20260511" ? 0.5 : 0.3);
  }, [selectedWeek]);

  const parseIds = (value) =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);

  const handleConfigInputChange = (e) => {
    const { name, value } = e.target;
    setConfigForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    setError(null);
    try {
      const payload = {
        partner_name: configForm.partner_name,
        super_agent_ids: parseIds(configForm.super_agent_ids),
        club_ids: parseIds(configForm.club_ids),
        mixed_club_ids: parseIds(configForm.mixed_club_ids),
        description: configForm.description,
        status: configForm.status,
      };

      const res = await axios.put(`${API_BASE}/partners/${ECOBOT_PARTNER_ID}`, payload);
      if (res.data.code === 1) {
        setSuccess("ECOBOT 配置已更新");
        setConfigEditMode(false);
        const fresh = await axios.get(`${API_BASE}/partners/${ECOBOT_PARTNER_ID}`);
        if (fresh.data.code === 1) {
          setConfig(fresh.data.data || null);
        }
      } else {
        setError(res.data.msg || "更新配置失敗");
      }
    } catch (err) {
      setError("更新配置失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setConfigSaving(false);
    }
  };

  const openConfigEditMode = () => {
    if (!config) return;
    setConfigForm({
      partner_name: config.partner_name || "",
      super_agent_ids: (config.super_agent_ids || []).join(", "),
      club_ids: (config.club_ids || []).join(", "),
      mixed_club_ids: (config.mixed_club_ids || []).join(", "),
      description: config.description || "",
      status: config.status || "active",
    });
    setConfigEditMode(true);
  };

  const renderIdBadges = (items, color) => {
    if (!items || items.length === 0) {
      return <span style={{ color: "#9A9A9A", fontSize: "0.9em" }}>未設定</span>;
    }
    return (
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {items.map((id, idx) => (
          <Badge key={`${id}-${idx}`} color={color} style={{ fontSize: "0.85em", padding: "6px 10px" }}>
            {id}
          </Badge>
        ))}
      </div>
    );
  };

  // 生成趨勢圖表數據
  const generateTrendChartData = (gameType, metric) => {
    if (!trendData || trendData.length === 0) return null;
    
    const reversedData = [...trendData].reverse();
    const config = gameTypeConfig[gameType];
    
    return {
      labels: reversedData.map(d => d.week),
      datasets: [{
        label: `${config.name} ${metric === 'rake' ? '服務費' : metric === 'pl' ? '輸贏' : '水前輸贏'}`,
        data: reversedData.map(d => d.total?.[gameType]?.[metric] || 0),
        borderColor: config.color,
        backgroundColor: config.bgColor,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: config.color,
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }]
    };
  };

  // 生成餅圖數據（遊戲類型分布）
  const generateGameTypePieData = () => {
    if (!weekStats || !weekStats.total) return null;
    
    const total = weekStats.total;
    const data = [
      Math.abs(total.ring_game?.pre_rake_pl || 0),
      Math.abs(total.aof?.pre_rake_pl || 0),
      Math.abs(total.sng?.pre_rake_pl || 0),
      Math.abs(total.mtt?.pre_rake_pl || 0),
    ];
    
    return {
      labels: ['NLH/PLO', 'AOF', 'SNG', 'MTT'],
      datasets: [{
        data: data,
        backgroundColor: [
          gameTypeConfig.ring_game.color,
          gameTypeConfig.aof.color,
          gameTypeConfig.sng.color,
          gameTypeConfig.mtt.color,
        ],
        borderWidth: 0,
        hoverOffset: 10
      }]
    };
  };

  // 生成俱樂部對比數據
  const generateClubComparisonData = () => {
    if (!weekStats || !weekStats.by_club) return null;
    
    const clubs = Object.keys(weekStats.by_club);
    
    return {
      labels: clubs,
      datasets: [
        {
          label: '服務費',
          data: clubs.map(club => {
            const clubData = weekStats.by_club[club];
            return (clubData.ring_game?.rake || 0) + (clubData.aof?.rake || 0) + 
                   (clubData.sng?.rake || 0) + (clubData.mtt?.rake || 0);
          }),
          backgroundColor: '#1f8ef1',
        },
        {
          label: '輸贏',
          data: clubs.map(club => {
            const clubData = weekStats.by_club[club];
            return (clubData.ring_game?.pl || 0) + (clubData.aof?.pl || 0) + 
                   (clubData.sng?.pl || 0) + (clubData.mtt?.pl || 0);
          }),
          backgroundColor: '#00d6b4',
        }
      ]
    };
  };

  // 渲染數據表格
  const renderGameTypeTable = (data, title) => {
    if (!data) return null;
    
    const gameTypes = [
      { key: 'ring_game', name: 'NLH/PLO', color: gameTypeConfig.ring_game.color },
      { key: 'aof', name: 'AOF', color: gameTypeConfig.aof.color },
      { key: 'sng', name: 'SNG', color: gameTypeConfig.sng.color },
      { key: 'mtt', name: 'MTT', color: gameTypeConfig.mtt.color }
    ];
    
    return (
      <Card>
        <CardHeader>
          <CardTitle tag="h4">{title}</CardTitle>
        </CardHeader>
        <CardBody>
          <Table responsive className="table-hover">
            <thead className="text-primary">
              <tr>
                <th>遊戲類型</th>
                <th className="text-right">服務費</th>
                <th className="text-right">輸贏</th>
                <th className="text-right">水前輸贏</th>
              </tr>
            </thead>
            <tbody>
              {gameTypes.map(gt => (
                <tr key={gt.key}>
                  <td>
                    <span style={{ 
                      display: 'inline-block', 
                      width: '10px', 
                      height: '10px', 
                      backgroundColor: gt.color, 
                      marginRight: '8px',
                      borderRadius: '2px'
                    }}></span>
                    {gt.name}
                  </td>
                  <td className="text-right">
                    <span style={{ color: '#00d6b4' }}>
                      {numberWithCommas(data[gt.key]?.rake || 0)}
                    </span>
                  </td>
                  <td className="text-right">
                    <span style={{ color: (data[gt.key]?.pl || 0) >= 0 ? '#00d6b4' : '#fd5d93' }}>
                      {numberWithCommas(data[gt.key]?.pl || 0)}
                    </span>
                  </td>
                  <td className="text-right">
                    <span style={{ 
                      color: (data[gt.key]?.pre_rake_pl || 0) >= 0 ? '#00d6b4' : '#fd5d93',
                      fontWeight: 'bold'
                    }}>
                      {numberWithCommas(data[gt.key]?.pre_rake_pl || 0)}
                    </span>
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #444' }}>
                <td>總計</td>
                <td className="text-right">
                  <span style={{ color: '#00d6b4', fontSize: '1.1em' }}>
                    {numberWithCommas(
                      gameTypes.reduce((sum, gt) => sum + (data[gt.key]?.rake || 0), 0)
                    )}
                  </span>
                </td>
                <td className="text-right">
                  <span style={{ 
                    color: gameTypes.reduce((sum, gt) => sum + (data[gt.key]?.pl || 0), 0) >= 0 ? '#00d6b4' : '#fd5d93',
                    fontSize: '1.1em'
                  }}>
                    {numberWithCommas(
                      gameTypes.reduce((sum, gt) => sum + (data[gt.key]?.pl || 0), 0)
                    )}
                  </span>
                </td>
                <td className="text-right">
                  <span style={{ 
                    color: gameTypes.reduce((sum, gt) => sum + (data[gt.key]?.pre_rake_pl || 0), 0) >= 0 ? '#00d6b4' : '#fd5d93',
                    fontSize: '1.1em'
                  }}>
                    {numberWithCommas(
                      gameTypes.reduce((sum, gt) => sum + (data[gt.key]?.pre_rake_pl || 0), 0)
                    )}
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>
        </CardBody>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="content">
        <TinyLoading variant="page" text="載入中" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <Alert color="danger">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="content">
      {success && (
        <Alert color="success" isOpen={!!success}>
          {success}
        </Alert>
      )}
      {/*
        改動前這一區是 Card > CardHeader 裡塞了「標題 ＋ 週選擇器」一列、
        下面再手刻一排 Nav tabs（active 色寫死 #1f8ef1，跟其他頁都不一樣）。
        現在身分 / 範圍 / 視角交給公版排列，tab 樣式也統一走 TinyPageTabs。
      */}
      <TinyPageHeader
        title="EcoBot 數據分析"
        description="EcoBot 夥伴俱樂部的服務費、輸贏與水前輸贏，以及每週應交收金額的計算依據。"
        scope={
          <TinyWeekSelector
            label=""
            options={weeks}
            value={selectedWeek}
            onChange={setSelectedWeek}
            showMeta={false}
            inputStyle={{ backgroundColor: "#27293d", border: "1px solid #2b3553" }}
          />
        }
        tabs={{
          items: [
            { key: "overview", label: "總覽", icon: "fa fa-dashboard" },
            { key: "by_club", label: "按俱樂部", icon: "fa fa-building" },
            { key: "by_super_agent", label: "按超代", icon: "fa fa-users" },
            { key: "config", label: "配置管理", icon: "fa fa-cog" },
            { key: "trend", label: "趨勢分析", icon: "fa fa-line-chart" },
          ],
          active: activeTab,
          onChange: setActiveTab,
        }}
      />

      <TabContent activeTab={activeTab}>
        <TabPane tabId="overview">
          <Row>
            <Col md="8">
              {weekStats && renderGameTypeTable(weekStats.total, `總覽 - ${selectedWeek}`)}
            </Col>
            <Col md="4">
              <Card>
                <CardHeader>
                  <CardTitle tag="h4">
                    <i className="fa fa-pie-chart mr-2"></i>遊戲類型分布（水前輸贏）
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div style={{ height: '320px' }}>
                    {weekStats && generateGameTypePieData() && (
                      <Pie data={generateGameTypePieData()} options={pieChartOptions} />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          
          {/* 交收數據計算 */}
          {weekStats && weekStats.total && (
            <Row className="mt-3">
              <Col md="12">
                <Card style={{ backgroundColor: 'rgba(0, 214, 180, 0.05)', border: '1px solid #00d6b4' }}>
                  <CardHeader>
                    <CardTitle tag="h4">
                      <i className="fa fa-calculator mr-2"></i>EcoBot 交收數據
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    {(() => {
                      const total = weekStats.total;
                      
                      // 公式各部分
                      // 1. (((NLH+PLO) + AOF) 的水前輸贏 + JP FEE - JP PAYOUT) * RG佔成
                      //    RG佔成：20260824 之前 30%、當週起 27%
                      //    (+ JP FEE - JP PAYOUT) 是把 JP 從 WIN 扣乾淨，不是 JP 佔成
                      const rgNetRate = getRgNetRate(selectedWeek, rateSettings);
                      const rgNetRatePct = parseFloat((rgNetRate * 100).toFixed(2)).toString();
                      const ringAofPreRakePl = (total.ring_game?.pre_rake_pl || 0) + (total.aof?.pre_rake_pl || 0);
                      const jpFee = total.jp_fee || 0;
                      const jpPayout = total.jp_payout || 0;
                      const part1 = (ringAofPreRakePl + jpFee - jpPayout) * rgNetRate;
                      
                      // 2. MTT 水前輸贏 * 50%
                      const mttPreRakePl = total.mtt?.pre_rake_pl || 0;
                      const part2 = mttPreRakePl * 0.50;
                      
                      // 3. (真人NLH+PLO總水 + 真人AOF總水) * 5%
                      //    ⚠️ 這裡的「真人水」是**對 EcoBot 帳單**的口徑 = 聯盟 − eco，
                      //    **含自營Bot**（對 EcoBot 而言自營Bot 就是真人，照抽 5%）。
                      //    API 端 `/ecobot/stats/<week>` 用 union_breakdown − eco_breakdown
                      //    餵這個欄位；不是營收頁那個扣掉自營Bot 的對內口徑。
                      const realTotalRake = weekStats.real_total_rake || {};
                      const realRingRake = realTotalRake.ring_game || 0;
                      const realAofRake = realTotalRake.aof || 0;
                      const part3 = (realRingRake + realAofRake) * 0.05;

                      // 4. 保險全數歸還（20260223 起）
                      // raw 符號：負 = bot 玩家在保險上淨虧（平台收到錢，要退給 EcoBot）
                      //           正 = bot 玩家在保險上淨賺（平台付出，EcoBot 要退回）
                      // 「Tiny 應付 EcoBot」= -insurance_total
                      const insuranceTotalRaw = total.insurance_total || 0;
                      const insuranceTotal = -insuranceTotalRaw;
                      const isInsuranceReturnWeek = selectedWeek >= '20260223-20260301';
                      const part4 = isInsuranceReturnWeek ? insuranceTotal : 0;
                      
                      // 總計
                      const totalSettlement = part1 + part2 + part3 + part4;
                      
                      return (
                        <>
                          <div style={{ fontSize: '0.95em', marginBottom: '25px' }}>
                            <h5 style={{ color: '#00d6b4', marginBottom: '20px' }}>
                              <i className="fa fa-info-circle mr-2"></i>計算公式
                            </h5>
                            <div style={{ 
                              backgroundColor: 'rgba(0,0,0,0.3)', 
                              padding: '20px', 
                              borderRadius: '8px',
                              border: '1px solid rgba(0, 214, 180, 0.3)'
                            }}>
                              <div style={{ marginBottom: '12px' }}>
                                <Badge color="info" style={{ marginRight: '10px', fontSize: '0.85em' }}>第一部分</Badge>
                                <span style={{ color: '#9A9A9A' }}>
                                  (((NLH/PLO) + AOF) 的水前輸贏 + JP FEE - JP PAYOUT) × {rgNetRatePct}%
                                </span>
                              </div>
                              <div style={{ marginBottom: '12px' }}>
                                <Badge color="success" style={{ marginRight: '10px', fontSize: '0.85em' }}>第二部分</Badge>
                                <span style={{ color: '#9A9A9A' }}>
                                  MTT 水前輸贏 × 50%
                                </span>
                              </div>
                              <div>
                                <Badge color="warning" style={{ marginRight: '10px', fontSize: '0.85em' }}>第三部分</Badge>
                                <span style={{ color: '#9A9A9A' }}>
                                  (真人NLH/PLO總水 + 真人AOF總水) × 5%
                                </span>
                              </div>
                              {isInsuranceReturnWeek && (
                                <div style={{ marginTop: '12px' }}>
                                  <Badge color="danger" style={{ marginRight: '10px', fontSize: '0.85em' }}>第四部分</Badge>
                                  <span style={{ color: '#9A9A9A' }}>
                                    保險全數歸還
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <Table responsive style={{ marginBottom: 0 }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #2b3553' }}>
                                <th style={{ width: '40%' }}>項目</th>
                                <th className="text-right" style={{ width: '30%' }}>計算</th>
                                <th className="text-right" style={{ width: '30%' }}>金額</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{ backgroundColor: 'rgba(31, 142, 241, 0.05)' }}>
                                <td>
                                  <Badge color="info" style={{ marginRight: '8px' }}>1</Badge>
                                  (Ring+AOF 水前 + JP) × {rgNetRatePct}%
                                </td>
                                <td className="text-right" style={{ fontSize: '0.85em', color: '#9A9A9A' }}>
                                  ({numberWithCommas(total.ring_game?.pre_rake_pl || 0)} + {numberWithCommas(total.aof?.pre_rake_pl || 0)} + {numberWithCommas(jpFee)} - {numberWithCommas(jpPayout)}) × {rgNetRatePct}%
                                </td>
                                <td className="text-right">
                                  <span style={{ color: '#1f8ef1', fontWeight: 'bold' }}>
                                    {numberWithCommas(part1)}
                                  </span>
                                </td>
                              </tr>
                              
                              <tr style={{ backgroundColor: 'rgba(0, 214, 180, 0.05)' }}>
                                <td>
                                  <Badge color="success" style={{ marginRight: '8px' }}>2</Badge>
                                  MTT 水前 × 50%
                                </td>
                                <td className="text-right" style={{ fontSize: '0.85em', color: '#9A9A9A' }}>
                                  {numberWithCommas(mttPreRakePl)} × 50%
                                </td>
                                <td className="text-right">
                                  <span style={{ color: '#00d6b4', fontWeight: 'bold' }}>
                                    {numberWithCommas(part2)}
                                  </span>
                                </td>
                              </tr>
                              
                              <tr style={{ backgroundColor: 'rgba(241, 126, 93, 0.05)' }}>
                                <td>
                                  <Badge color="warning" style={{ marginRight: '8px' }}>3</Badge>
                                  真人總水 × 5%
                                </td>
                                <td className="text-right" style={{ fontSize: '0.85em', color: '#9A9A9A' }}>
                                  ({numberWithCommas(realRingRake)} + {numberWithCommas(realAofRake)}) × 5%
                                </td>
                                <td className="text-right">
                                  <span style={{ color: '#f17e5d', fontWeight: 'bold' }}>
                                    {numberWithCommas(part3)}
                                  </span>
                                </td>
                              </tr>

                              {isInsuranceReturnWeek && (
                                <tr style={{ backgroundColor: 'rgba(253, 93, 147, 0.08)' }}>
                                  <td>
                                    <Badge color="danger" style={{ marginRight: '8px' }}>4</Badge>
                                    保險全數歸還
                                  </td>
                                  <td className="text-right" style={{ fontSize: '0.85em', color: '#9A9A9A' }}>
                                    {numberWithCommas(insuranceTotal)}
                                  </td>
                                  <td className="text-right">
                                    <span style={{ color: part4 >= 0 ? '#00d6b4' : '#fd5d93', fontWeight: 'bold' }}>
                                      {numberWithCommas(part4)}
                                    </span>
                                  </td>
                                </tr>
                              )}
                              
                              <tr style={{ 
                                fontWeight: 'bold', 
                                fontSize: '1.15em',
                                borderTop: '3px solid #00d6b4',
                                backgroundColor: 'rgba(0, 214, 180, 0.1)'
                              }}>
                                <td colSpan="2" style={{ color: '#00d6b4' }}>
                                  <i className="fa fa-dollar mr-2"></i>應交收金額總計
                                </td>
                                <td className="text-right" style={{ color: '#00d6b4', fontSize: '1.3em' }}>
                                  {numberWithCommas(totalSettlement)}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                          
                          <div style={{ 
                            marginTop: '20px', 
                            padding: '15px', 
                            backgroundColor: 'rgba(253, 93, 147, 0.1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(253, 93, 147, 0.3)'
                          }}>
                            <div style={{ fontSize: '0.9em', color: '#9A9A9A' }}>
                              <i className="fa fa-lightbulb mr-2" style={{ color: '#fd5d93' }}></i>
                              <strong style={{ color: '#fd5d93' }}>說明：</strong>
                              真人總水數據來自「營收數據」頁面的 weekly_revenue_summary，
                              請對照「營收數據」頁面進行驗算。
                            </div>
                          </div>

                          {selectedWeek >= '20260223-20260301' && (() => {
                            // 5/11 起降波動可切 30%/50%（預設 50%）；之前的週固定 30%
                            const canSwitchJpRate = (selectedWeek || '').split('-')[0] >= '20260511';
                            const jpRate = canSwitchJpRate ? jpVolRate : 0.30;
                            const jpRatePct = Math.round(jpRate * 100);
                            const ecoJpFeeAdj = jpFee * jpRate;
                            const ecoJpPayoutAdj = jpPayout * jpRate;
                            const dealAdj = ecoJpPayoutAdj - ecoJpFeeAdj;
                            const weeklyTotal = totalSettlement + dealAdj;
                            return (
                              <>
                              <div style={{
                                marginTop: '25px',
                                padding: '20px',
                                backgroundColor: 'rgba(225, 78, 202, 0.06)',
                                borderRadius: '8px',
                                border: '1px solid rgba(225, 78, 202, 0.35)',
                              }}>
                                <h5 style={{ color: '#e14eca', marginBottom: '15px' }}>
                                  <i className="fa fa-shield mr-2"></i>
                                  聯盟JP降波動
                                  <span style={{ fontSize: '0.65em', color: '#9A9A9A', marginLeft: 10, fontWeight: 400 }}>
                                    20260223 起生效
                                  </span>
                                </h5>
                                {canSwitchJpRate && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 15, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '0.85em', color: '#9A9A9A' }}>降波動比例</span>
                                    <div style={{
                                      display: 'inline-flex',
                                      gap: 3,
                                      padding: 3,
                                      borderRadius: 10,
                                      backgroundColor: 'rgba(0,0,0,0.35)',
                                      border: '1px solid rgba(225,78,202,0.3)',
                                    }}>
                                      {[30, 50].map((p) => {
                                        const active = jpRatePct === p;
                                        return (
                                          <div
                                            key={p}
                                            onClick={() => setJpVolRate(p / 100)}
                                            style={{
                                              cursor: 'pointer',
                                              userSelect: 'none',
                                              padding: '7px 24px',
                                              borderRadius: 8,
                                              background: active ? '#e14eca' : 'transparent',
                                              color: active ? '#fff' : '#7d7d92',
                                              fontWeight: active ? 'bold' : 500,
                                              fontSize: '1em',
                                              boxShadow: active ? '0 0 12px rgba(225,78,202,0.55)' : 'none',
                                              transition: 'all 0.15s ease',
                                            }}
                                          >
                                            {active && <i className="fa fa-check mr-1" style={{ fontSize: '0.85em' }} />}
                                            {p}%
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <span style={{ fontSize: '0.85em', color: '#e14eca', fontWeight: 'bold' }}>
                                      目前採用 {jpRatePct}%
                                    </span>
                                  </div>
                                )}
                                <Table responsive style={{ marginBottom: 0 }}>
                                  <thead>
                                    <tr style={{ borderBottom: '2px solid #2b3553' }}>
                                      <th style={{ width: '50%' }}>項目</th>
                                      <th className="text-right" style={{ width: '25%' }}>原始金額</th>
                                      <th className="text-right" style={{ width: '25%' }}>交收調整</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>
                                        <span style={{ color: '#e14eca' }}>EcoBot 負擔 JP FEE（{jpRatePct}%）</span>
                                        <div style={{ fontSize: '0.78em', color: '#888', marginTop: 2 }}>
                                          EcoBot 自行承擔，交收金額扣除
                                        </div>
                                      </td>
                                      <td className="text-right" style={{ color: '#9A9A9A' }}>
                                        {numberWithCommas(jpFee)}
                                      </td>
                                      <td className="text-right">
                                        <span style={{ color: '#fd5d93', fontWeight: 'bold' }}>
                                          -{numberWithCommas(ecoJpFeeAdj)}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>
                                        <span style={{ color: '#e14eca' }}>EcoBot 拿取 JP PAYOUT（{jpRatePct}%）</span>
                                        <div style={{ fontSize: '0.78em', color: '#888', marginTop: 2 }}>
                                          EcoBot 分得中獎金，交收金額增加
                                        </div>
                                      </td>
                                      <td className="text-right" style={{ color: '#9A9A9A' }}>
                                        {numberWithCommas(jpPayout)}
                                      </td>
                                      <td className="text-right">
                                        <span style={{ color: '#00d6b4', fontWeight: 'bold' }}>
                                          +{numberWithCommas(ecoJpPayoutAdj)}
                                        </span>
                                      </td>
                                    </tr>
                                    <tr style={{
                                      fontWeight: 'bold',
                                      borderTop: '3px solid #e14eca',
                                      backgroundColor: 'rgba(225, 78, 202, 0.08)',
                                    }}>
                                      <td colSpan="2" style={{ color: '#e14eca' }}>
                                        <i className="fa fa-calculator mr-2"></i>
                                        聯盟JP降波動金額
                                      </td>
                                      <td className="text-right" style={{
                                        color: dealAdj >= 0 ? '#00d6b4' : '#fd5d93',
                                        fontWeight: 'bold',
                                        fontSize: '1.15em',
                                      }}>
                                        {numberWithCommas(dealAdj)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                              </div>

                              <div style={{
                                marginTop: '20px',
                                padding: '20px',
                                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                borderRadius: '8px',
                                border: '2px solid #e14eca',
                              }}>
                                <div style={{ marginBottom: '16px' }}>
                                  <h5 style={{ color: '#fff', marginBottom: '10px' }}>
                                    <i className="fa fa-dollar mr-2" style={{ color: '#e14eca' }}></i>
                                    當周應交收總額
                                  </h5>
                                  <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                    borderRadius: '6px',
                                    fontSize: '0.9em',
                                    color: '#9A9A9A',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                  }}>
                                    <span style={{ color: '#00d6b4' }}>聯盟應交收金額</span>
                                    {' + '}
                                    <span style={{ color: '#e14eca' }}>聯盟JP降波動</span>
                                    {' = '}
                                    <span style={{ color: '#fff' }}>當周應交收總額</span>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                  <div style={{ textAlign: 'center', padding: '12px 20px', backgroundColor: 'rgba(0, 214, 180, 0.08)', borderRadius: '8px', border: '1px solid rgba(0, 214, 180, 0.3)', minWidth: '160px' }}>
                                    <div style={{ fontSize: '0.78em', color: '#9A9A9A', marginBottom: 4 }}>聯盟應交收金額</div>
                                    <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#00d6b4' }}>{numberWithCommas(totalSettlement)}</div>
                                  </div>
                                  <div style={{ fontSize: '1.5em', color: '#9A9A9A', fontWeight: 'bold' }}>+</div>
                                  <div style={{ textAlign: 'center', padding: '12px 20px', backgroundColor: 'rgba(225, 78, 202, 0.08)', borderRadius: '8px', border: '1px solid rgba(225, 78, 202, 0.3)', minWidth: '160px' }}>
                                    <div style={{ fontSize: '0.78em', color: '#9A9A9A', marginBottom: 4 }}>聯盟JP降波動</div>
                                    <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: dealAdj >= 0 ? '#00d6b4' : '#fd5d93' }}>{numberWithCommas(dealAdj)}</div>
                                  </div>
                                  <div style={{ fontSize: '1.5em', color: '#9A9A9A', fontWeight: 'bold' }}>=</div>
                                  <div style={{ textAlign: 'center', padding: '14px 24px', backgroundColor: 'rgba(225, 78, 202, 0.12)', borderRadius: '8px', border: '2px solid #e14eca', minWidth: '180px' }}>
                                    <div style={{ fontSize: '0.78em', color: '#9A9A9A', marginBottom: 4 }}>當周應交收總額</div>
                                    <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#e14eca' }}>{numberWithCommas(weeklyTotal)}</div>
                                  </div>
                                </div>
                              </div>
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </TabPane>

        <TabPane tabId="by_club">
          <Row>
            {weekStats && weekStats.by_club && Object.keys(weekStats.by_club).length > 0 ? (
              <>
                {Object.entries(weekStats.by_club).map(([clubId, data]) => (
                  <Col md="12" key={clubId} className="mb-3">
                    {renderGameTypeTable(data, `俱樂部 ${clubId}`)}
                  </Col>
                ))}
                <Col md="12">
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4">
                        <i className="fa fa-bar-chart mr-2"></i>俱樂部對比
                      </CardTitle>
                    </CardHeader>
                    <CardBody>
                      <div style={{ height: '350px' }}>
                        {generateClubComparisonData() && (
                          <Bar data={generateClubComparisonData()} options={barChartOptions} />
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </>
            ) : (
              <Col md="12">
                <Alert color="info">該週沒有按俱樂部分組的數據</Alert>
              </Col>
            )}
          </Row>
        </TabPane>

        <TabPane tabId="by_super_agent">
          <Row>
            {weekStats && weekStats.by_super_agent && Object.keys(weekStats.by_super_agent).length > 0 ? (
              Object.entries(weekStats.by_super_agent).map(([saId, data]) => (
                <Col md="12" key={saId} className="mb-3">
                  {renderGameTypeTable(data, `超代 ${saId}`)}
                </Col>
              ))
            ) : (
              <Col md="12">
                <Alert color="info">該週沒有按超代分組的數據</Alert>
              </Col>
            )}
          </Row>
        </TabPane>

        <TabPane tabId="trend">
          <Row>
            {[
              { key: 'ring_game', name: 'NLH/PLO' },
              { key: 'aof', name: 'AOF' },
              { key: 'sng', name: 'SNG' },
              { key: 'mtt', name: 'MTT' }
            ].map(gameType => (
              <Col md="6" key={gameType.key} className="mb-4">
                <Card>
                  <CardHeader>
                    <CardTitle tag="h4">
                      <span style={{ 
                        display: 'inline-block', 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: gameTypeConfig[gameType.key].color, 
                        marginRight: '8px',
                        borderRadius: '3px'
                      }}></span>
                      {gameType.name} 趨勢
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div style={{ height: '280px' }}>
                      {generateTrendChartData(gameType.key, 'pre_rake_pl') && (
                        <Line 
                          data={generateTrendChartData(gameType.key, 'pre_rake_pl')} 
                          options={getLineChartOptions(gameTypeConfig[gameType.key].color)} 
                        />
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </TabPane>
        <TabPane tabId="config">
          <Row>
            <Col md="12">
              <Card style={{ backgroundColor: "rgba(31, 142, 241, 0.05)", border: "1px solid #1f8ef1" }}>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col md="8">
                      <CardTitle tag="h4" style={{ marginBottom: 0 }}>
                        <i className="fa fa-cog mr-2"></i>ECOBOT 配置管理
                      </CardTitle>
                      <p className="text-muted mb-0 mt-2">
                        當前 ECOBOT 合作夥伴配置 (v2.2.0：唯讀)
                      </p>
                    </Col>
                    <Col md="4" className="text-right">
                      <a href="/admin/tiny-clubs" style={{ fontSize: '0.9em', color: '#00d6b4' }}>
                        <i className="fa fa-external-link mr-1" />前往俱樂部管理編輯
                      </a>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {!config ? (
                    <Alert color="warning" className="mb-0">
                      找不到 ECOBOT 配置（partner_id: {ECOBOT_PARTNER_ID}）
                    </Alert>
                  ) : (
                    <>
                      <Alert color="info" className="mb-3" style={{ background: "rgba(31,142,241,0.12)", border: "1px solid rgba(31,142,241,0.4)", color: "#cbe5ff" }}>
                        <i className="fa fa-info-circle mr-2" />
                        v2.2.0：本頁改唯讀。EcoBot 的 club / 超代 / 直代標記請至「俱樂部管理」頁面，在對應的 club 編輯內勾選 <code>partner_id</code> / <code>ecobot_role</code> / <code>is_ecobot</code>；下次 pipeline 跑後生效。
                      </Alert>
                      <div
                        style={{
                          padding: "18px 20px",
                          borderRadius: "10px",
                          background: "linear-gradient(120deg, rgba(31,142,241,0.18), rgba(0,214,180,0.14))",
                          border: "1px solid rgba(31, 142, 241, 0.35)",
                          marginBottom: "16px",
                        }}
                      >
                        <Row className="align-items-center">
                          <Col md="8">
                            <div style={{ fontSize: "0.85em", color: "#9A9A9A", marginBottom: "6px" }}>
                              合作夥伴
                            </div>
                            <h4 style={{ margin: 0, color: "#fff" }}>
                              <i className="fa fa-handshake mr-2" />
                              {config.partner_name || "Ecobot"}
                            </h4>
                          </Col>
                          <Col md="4" className="text-right">
                            <Badge
                              color={config.status === "active" ? "success" : "secondary"}
                              style={{ fontSize: "0.9em", padding: "7px 12px" }}
                            >
                              {config.status === "active" ? "啟用中" : "停用中"}
                            </Badge>
                          </Col>
                        </Row>
                      </div>

                      <div style={{ marginBottom: "12px", color: "#9A9A9A", fontSize: "0.9em" }}>
                        <i className="fa fa-calendar mr-1" />週期：<strong style={{ color: "#fff" }}>{snapshotConfig?.week || selectedWeek || '—'}</strong>
                        <small style={{ marginLeft: 12 }}>（依當週 pipeline 跑時 snapshot 顯示，切換上方週期看不同週設定）</small>
                      </div>
                      <Row>
                        <Col md="6" className="mb-3">
                          <div style={{ height: "100%", backgroundColor: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "16px" }}>
                            <h6 style={{ color: "#00d6b4", marginBottom: "12px" }}>
                              <i className="fa fa-building mr-2" />純 EcoBot 俱樂部 ({(snapshotConfig?.clubs?.pure || []).length})
                            </h6>
                            {(snapshotConfig?.clubs?.pure || []).length === 0 ? (
                              <span style={{ color: "#888" }}>—</span>
                            ) : (snapshotConfig.clubs.pure.map((c, idx) => (
                              <Badge key={idx} color="success" className="mr-1 mb-1" style={{ fontSize: "0.85em" }}>
                                {c.club_id}{c.club_name ? ` · ${c.club_name}` : ''}
                              </Badge>
                            )))}
                          </div>
                        </Col>
                        <Col md="6" className="mb-3">
                          <div style={{ height: "100%", backgroundColor: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "16px" }}>
                            <h6 style={{ color: "#f17e5d", marginBottom: "12px" }}>
                              <i className="fa fa-random mr-2" />混合俱樂部 ({(snapshotConfig?.clubs?.mixed || []).length})
                            </h6>
                            {(snapshotConfig?.clubs?.mixed || []).length === 0 ? (
                              <span style={{ color: "#888" }}>—</span>
                            ) : (snapshotConfig.clubs.mixed.map((c, idx) => (
                              <Badge key={idx} color="warning" className="mr-1 mb-1" style={{ fontSize: "0.85em" }}>
                                {c.club_id}{c.club_name ? ` · ${c.club_name}` : ''}
                              </Badge>
                            )))}
                          </div>
                        </Col>
                        <Col md="6" className="mb-3">
                          <div style={{ height: "100%", backgroundColor: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "16px" }}>
                            <h6 style={{ color: "#1f8ef1", marginBottom: "12px" }}>
                              <i className="fa fa-user-secret mr-2" />EcoBot 超代 ({(snapshotConfig?.super_agents || []).length})
                            </h6>
                            {(snapshotConfig?.super_agents || []).length === 0 ? (
                              <span style={{ color: "#888" }}>—</span>
                            ) : (snapshotConfig.super_agents.map((sa, idx) => (
                              <div key={idx} style={{ marginBottom: 4 }}>
                                <Badge color="info" className="mr-1" style={{ fontSize: "0.85em" }}>
                                  {sa.super_id}{sa.nickname ? ` · ${sa.nickname}` : ''}
                                </Badge>
                                <small style={{ color: "#9A9A9A" }}>@ {sa.club_id} {sa.club_name && `(${sa.club_name})`}</small>
                              </div>
                            )))}
                          </div>
                        </Col>
                        <Col md="6" className="mb-3">
                          <div style={{ height: "100%", backgroundColor: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "16px" }}>
                            <h6 style={{ color: "#fd5d93", marginBottom: "12px" }}>
                              <i className="fa fa-user mr-2" />EcoBot 直代 ({(snapshotConfig?.direct_agents || []).length})
                            </h6>
                            {(snapshotConfig?.direct_agents || []).length === 0 ? (
                              <span style={{ color: "#888" }}>—</span>
                            ) : (snapshotConfig.direct_agents.map((da, idx) => (
                              <div key={idx} style={{ marginBottom: 4 }}>
                                <Badge color="danger" className="mr-1" style={{ fontSize: "0.85em" }}>
                                  {da.agent_id}{da.nickname ? ` · ${da.nickname}` : ''}
                                </Badge>
                                <small style={{ color: "#9A9A9A" }}>@ {da.club_id} {da.club_name && `(${da.club_name})`}</small>
                              </div>
                            )))}
                          </div>
                        </Col>
                        <Col md="12" className="mb-3">
                          <div style={{ backgroundColor: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "16px" }}>
                            <h6 style={{ color: "#9A9A9A", marginBottom: "8px" }}>
                              <i className="fa fa-note-sticky mr-2" />Partner 說明
                            </h6>
                            <div style={{ color: "#D1D5DB", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                              {config.description || "（無）"}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* EcoBot cash 佔成設定：新費率 + 生效起始週。
              未設生效週 = 尚未生效，一律沿用歷史合約 30%（fail-closed）。
              ⚠️ 這不是「聯盟JP降波動」30%/50%（jpVolRate），那是 JP 自己的佔成，兩者互不相干。 */}
          <Row>
            <Col md="12">
              <Card style={{ backgroundColor: "rgba(225, 78, 202, 0.05)", border: "1px solid #e14eca" }}>
                <CardHeader>
                  <CardTitle tag="h4" style={{ marginBottom: 0 }}>
                    <i className="fa fa-percent mr-2"></i>EcoBot cash 佔成設定（Ring Game 水前輸贏扣除JP）
                  </CardTitle>
                  <p className="text-muted mb-0 mt-2">
                    設定新費率與生效起始週。生效週<strong>當週(含)之後</strong>才套新費率，
                    之前的週一律用歷史合約 {(RG_HISTORICAL_RATE * 100).toFixed(0)}% —— 已結的帳不會被改掉。
                  </p>
                </CardHeader>
                <CardBody>
                  <Alert color="info" className="mb-3" style={{ background: "rgba(225,78,202,0.12)", border: "1px solid rgba(225,78,202,0.4)", color: "#f5d0ee" }}>
                    <i className="fa fa-exclamation-triangle mr-2" />
                    這裡設定的是 <strong>cash 佔成</strong>（<code>(Ring+AOF 水前 + JP Fee − JP Payout) × 佔成</code>），
                    與總覽頁的「<strong>聯盟JP降波動</strong>」30%/50% 是<strong>兩件不同的事</strong> —— 那個是 JP 本身要佔多少，不受這裡影響。
                  </Alert>

                  <div
                    style={{
                      padding: "18px 20px",
                      borderRadius: "10px",
                      background: "linear-gradient(120deg, rgba(225,78,202,0.18), rgba(31,142,241,0.14))",
                      border: "1px solid rgba(225, 78, 202, 0.35)",
                      marginBottom: "16px",
                    }}
                  >
                    <Row className="align-items-center">
                      <Col md="8">
                        <div style={{ fontSize: "0.85em", color: "#9A9A9A", marginBottom: "6px" }}>
                          本頁所選週期（{selectedWeek || "—"}）實際套用
                        </div>
                        <h4 style={{ margin: 0, color: "#fff" }}>
                          <i className="fa fa-calculator mr-2" />
                          {(getRgNetRate(selectedWeek, rateSettings) * 100).toFixed(2)}%
                        </h4>
                      </Col>
                      <Col md="4" className="text-right">
                        <Badge
                          color={rateSettings && rateSettings.effective_from_week ? "success" : "secondary"}
                          style={{ fontSize: "0.9em", padding: "7px 12px" }}
                        >
                          {rateSettings && rateSettings.effective_from_week
                            ? `新費率自 ${rateSettings.effective_from_week} 起生效`
                            : "尚未設生效週 · 全部沿用 30%"}
                        </Badge>
                      </Col>
                    </Row>
                  </div>

                  <div style={{ backgroundColor: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "16px" }}>
                    <h6 style={{ color: "#e14eca", marginBottom: "14px" }}>
                      <i className="fa fa-pencil mr-2" />修改設定
                    </h6>
                    <Row>
                      <Col md="3">
                        <FormGroup>
                          <Label style={{ color: "#9A9A9A", fontSize: "0.85em" }}>新費率（%）</Label>
                          <Input
                            type="number" min="0" max="100" step="0.01"
                            placeholder="例如 27"
                            value={rateDraft.new_rate}
                            onChange={(e) => setRateDraft({ ...rateDraft, new_rate: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label style={{ color: "#9A9A9A", fontSize: "0.85em" }}>
                            生效起始週（留空 = 尚未生效）
                          </Label>
                          <Input
                            type="text"
                            list="ecobot-rate-week-list"
                            placeholder="YYYYMMDD-YYYYMMDD"
                            value={rateDraft.effective_from_week}
                            onChange={(e) => setRateDraft({ ...rateDraft, effective_from_week: e.target.value })}
                            invalid={!!rateDraft.effective_from_week && !WEEK_ID_RE.test(rateDraft.effective_from_week)}
                          />
                          <datalist id="ecobot-rate-week-list">
                            {weeks.map((w) => <option key={w} value={w} />)}
                          </datalist>
                        </FormGroup>
                      </Col>
                      <Col md="5">
                        <FormGroup>
                          <Label style={{ display: "block", visibility: "hidden" }}>儲存</Label>
                          <button
                            className="btn btn-primary"
                            style={{ backgroundColor: "#e14eca", borderColor: "#e14eca" }}
                            disabled={rateSaving || (!!rateDraft.effective_from_week && !WEEK_ID_RE.test(rateDraft.effective_from_week))}
                            onClick={saveRateSettings}
                          >
                            {rateSaving ? <><TinyBusy light style={{ marginRight: 4 }} /> 儲存中</> : <><i className="fa fa-save mr-1" />儲存設定</>}
                          </button>
                        </FormGroup>
                      </Col>
                    </Row>
                    {rateMsg && (
                      <Alert color={rateMsg.ok ? "success" : "danger"} className="mb-2">
                        {rateMsg.text}
                      </Alert>
                    )}
                    <div style={{ fontSize: "0.8em", color: "#9A9A9A", marginBottom: 0 }}>
                      <i className="fa fa-info-circle mr-1" />
                      改完只影響「之後重跑 pipeline 的週」的報表數字。已產出的報表要重跑該週才會變。
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </div>
  );
}

export default TinyEcoBot;
